// Weekly slots of a group plan (SPEC §7.3): a new slot starts at 16:00 and lasts the default length; changing the
// start moves the end along; the length is shown next to each slot.

import type { Slot, Weekday } from "../api/types";
import { formatWeekdayShort, listWeekdays, t } from "../i18n/i18n";
import { defaultRange, CELLS_PER_HOUR } from "../time";
import { createButton, createElement } from "./dom";
import { createSelect } from "./forms";
import { createTimeRangePicker, type TimeRangePicker } from "./timeRangePicker";

const NEW_SLOT_START_CELL: number = 16 * CELLS_PER_HOUR;

interface SlotRow {
  slotId: string | null;
  daySelect: HTMLSelectElement;
  picker: TimeRangePicker;
  element: HTMLElement;
}

export interface SlotsEditor {
  element: HTMLElement;
  getSlots: () => Slot[];
}

/** An editor showing the slots, with "Add slot". */
export function createSlotsEditor(initialSlots: Slot[], defaultLessonCells: number): SlotsEditor {
  const rows: SlotRow[] = [];
  const list: HTMLDivElement = createElement("div", "slots-list");
  const addRow = (slot: Slot): void => {
    const row: SlotRow = createSlotRow(slot, (): void => {
      rows.splice(rows.indexOf(row), 1);
      row.element.remove();
    });
    rows.push(row);
    list.append(row.element);
  };
  initialSlots.forEach(addRow);
  const addButton: HTMLButtonElement = createButton(t("groups.addSlot"), (): void => {
    addRow({ slotId: null, day: "Monday", range: defaultRange(NEW_SLOT_START_CELL, defaultLessonCells) });
  });
  const element: HTMLDivElement = createElement("div", "slots-editor");
  element.append(createElement("span", "field-label", t("groups.slots")), list, addButton);
  return { element, getSlots: (): Slot[] => rows.map(readSlot) };
}

function createSlotRow(slot: Slot, onRemove: () => void): SlotRow {
  const options: { value: string; label: string }[] = listWeekdays().map(
    (day: string): { value: string; label: string } => ({ value: day, label: formatWeekdayShort(day) }));
  const daySelect: HTMLSelectElement = createSelect(options, slot.day);
  daySelect.setAttribute("aria-label", t("groups.day"));
  const picker: TimeRangePicker = createTimeRangePicker(slot.range);
  const element: HTMLDivElement = createElement("div", "slot-row");
  element.append(daySelect, picker.element, createButton(t("groups.removeSlot"), onRemove, "button-link"));
  return { slotId: slot.slotId, daySelect, picker, element };
}

function readSlot(row: SlotRow): Slot {
  return { slotId: row.slotId, day: row.daySelect.value as Weekday, range: row.picker.getRange() };
}
